import LocationIcon16 from '@sats-group/icons/16/location';
import Activity from '@sats-group/icons/18/activity';
import SvgDot from '@sats-group/icons/18/dot';
import SvgLink from '@sats-group/icons/18/link';
import LocationIcon18 from '@sats-group/icons/18/location';
import Parking from '@sats-group/icons/18/parking';
import Time from '@sats-group/icons/18/time';
import Button from '@sats-group/ui-lib/react/button/button';
import Chip from '@sats-group/ui-lib/react/chip';
import Expander from '@sats-group/ui-lib/react/expander';
import Link from '@sats-group/ui-lib/react/link';
import LinkButton from '@sats-group/ui-lib/react/link-button';
import Message from '@sats-group/ui-lib/react/message';
import Modal from '@sats-group/ui-lib/react/modal/modal';
import Text from '@sats-group/ui-lib/react/text';
import cn from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import ClubLoadIndicator from '~/client/components/club-load-indicator/club-load-indicator';
import ContentContainer from '~/client/components/content-container/content-container';
import DynamicImage from '~/client/components/dynamic-image/dynamic-image';
import EmbedYoutube from '~/client/components/embed-youtube/embed-youtube';
import GroupExerciseSchedule from '~/client/components/group-exercise-schedule/group-exercise-schedule';
import Layout from '~/client/components/layout/layout';
import LoadAndOpenStatus from '~/client/components/load-and-open-status/load-and-open-status';
import LocationCard from '~/client/components/location-card/location-card';
import Map from '~/client/components/map/map';
import type { OpenStatus } from '~/client/components/open-status/open-status.types';
import OpeningHours from '~/client/components/opening-hours/opening-hours';
import PageHeader from '~/client/components/page-header/page-header';
import RichText from '~/client/components/rich-text/rich-text';
import SegmentedSection from '~/client/components/segmented-section/segmented-section';
import Slideshow from '~/client/components/slideshow/slideshow';
import useClubLoadFactors from '~/client/hooks/use-club-load-factors';
import usePolling from '~/client/hooks/use-polling';
import type {
  ClubPage as Props,
  ServicesAndFacilitiesSection,
} from './club-page.types';
import {
  toBecomeMemberSegmentedSectionView,
  toConceptsSegmentedSectionView,
  toTeamSegmentedSectionView,
} from './to-segmented-section-view';

const slideshowId = 'club-page-slideshow';
export const slideshowHref = `#${slideshowId}`;

const mapId = 'club-page-map';
export const mapIdHref = `#${mapId}`;

const becomeMemberSectionId = 'become-member-section';
export const becomeMemberSectionHref = `#${becomeMemberSectionId}`;

const Section: React.FunctionComponent<
  React.PropsWithChildren<{
    centerHeader?: boolean;
    className?: string;
    eyebrow?: string;
    fullbleed?: boolean;
    horizontal?: boolean;
    onFixedBackground?: boolean;
    title?: string;
    titleLarge?: boolean;
  }>
> = ({
  centerHeader,
  className,
  children,
  eyebrow,
  fullbleed,
  horizontal = false,
  onFixedBackground,
  title,
  titleLarge = false,
}) => (
  <div
    className={cn(
      'club-page__section',
      {
        'club-page__section--fullbleed': fullbleed,
        'club-page__section--horizontal': horizontal,
      },
      className
    )}
  >
    {eyebrow || title ? (
      <div>
        {eyebrow ? (
          <Text
            className={cn('club-page__section-eyebrow', {
              'club-page__section-eyebrow--center-header': centerHeader,
              'club-page__section-eyebrow--fullbleed': fullbleed,
              'club-page__section-eyebrow--on-fixed-background':
                onFixedBackground,
            })}
          >
            {eyebrow}
          </Text>
        ) : null}
        {title ? (
          <Text
            className={cn('club-page__section-title', {
              'club-page__section-title--center-header': centerHeader,
              'club-page__section-title--fullbleed': fullbleed,
              'club-page__section-title--on-fixed-background':
                onFixedBackground,
            })}
            elementName="h2"
            size={titleLarge ? Text.sizes.headline1 : Text.sizes.headline3}
            theme={Text.themes.emphasis}
          >
            {title}
          </Text>
        ) : null}
      </div>
    ) : null}
    {children}
  </div>
);

const ClubMembershipSection: React.FunctionComponent<{
  brand: string;
  section: NonNullable<Props['becomeMemberSection']>;
}> = ({ brand, section }) => {
  const [firstView, secondView] = section.views;
  const sharedProps = {
    alignment: SegmentedSection.alignments.center,
    appearance: SegmentedSection.appearances.fixed,
    className: `club-page__become-member club-page__become-member--${brand}`,
    eyebrow: section.eyebrow,
    title: section.title,
  };

  return secondView ? (
    <SegmentedSection
      {...sharedProps}
      toggle={{
        label: section.toggleLabel,
        name: 'member-age-toggle',
        value: 'over-30',
      }}
      views={[
        toBecomeMemberSegmentedSectionView(firstView),
        toBecomeMemberSegmentedSectionView(secondView),
      ]}
    />
  ) : (
    <SegmentedSection
      {...sharedProps}
      views={[toBecomeMemberSegmentedSectionView(firstView)]}
    />
  );
};

const ClubPage: React.FunctionComponent<Props> = ({
  becomeMemberSection,
  brand,
  clubId,
  clubLoadEndpoint,
  concepts,
  ctaBanner,
  faqs = [],
  facilities,
  services,
  layout,
  loadAndOpenStatus,
  mediaGallery,
  messages,
  nearby,
  nearbyEyebrow,
  nearbyTitle,
  openStatusesEndpoint,
  pageHeader,
  planYourNextVisit,
  servicesAndFacilitiesEyebrow,
  servicesAndFacilitiesTitle,
  schedule,
  scheduleTitle,
  teamSection,
}) => {
  const [loadFactors] = useClubLoadFactors(clubLoadEndpoint);
  const [openStatuses = {}] =
    usePolling<Record<string, OpenStatus>>(openStatusesEndpoint);
  const { addressLink, ...pageHeaderProps } = pageHeader;
  const loadFactor = loadFactors ? loadFactors[clubId] : undefined;
  const openStatus = openStatuses ? openStatuses[clubId] : undefined;
  const ctaRef = useRef<HTMLDivElement>(null);
  const [isActionBarVisible, setIsActionBarVisible] = useState(false);
  const [selectedServicesAndFacilities, setSelectedServicesAndFacilities] =
    useState<ServicesAndFacilitiesSection>(() =>
      services ? 'services' : 'facilities'
    );

  const servicesAndFacilities =
    selectedServicesAndFacilities === 'facilities' ? facilities : services;
  const fullListMiddleIndex = servicesAndFacilities
    ? Math.ceil(servicesAndFacilities.fullList.length / 2)
    : 0;
  const fullListColumns = servicesAndFacilities
    ? [
        {
          id: 'left',
          items: servicesAndFacilities.fullList.slice(0, fullListMiddleIndex),
        },
        {
          id: 'right',
          items: servicesAndFacilities.fullList.slice(fullListMiddleIndex),
        },
      ].filter(column => column.items.length)
    : [];

  useEffect(() => {
    if (!ctaRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsActionBarVisible(!(entry && entry.isIntersecting)),
      { threshold: 0 }
    );

    observer.observe(ctaRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <Layout {...layout}>
      <div className="club-page">
        <div ref={ctaRef}>
          <PageHeader
            {...pageHeaderProps}
            slot={
              loadAndOpenStatus && loadFactor && openStatus ? (
                <div>
                  <LoadAndOpenStatus
                    loadFactor={loadFactor}
                    openStatus={openStatus}
                    {...loadAndOpenStatus}
                    background={LoadAndOpenStatus.backgrounds.fixed}
                  />
                </div>
              ) : addressLink ? (
                <div>
                  <Link
                    size={Link.sizes.small}
                    {...addressLink}
                    icon={<LocationIcon16 />}
                    background={Link.backgrounds.fixed}
                  />
                </div>
              ) : undefined
            }
          />
        </div>
        <ContentContainer theme="wide" className="club-page__block">
          {messages.length ? (
            <div className="club-page__messages">
              {messages.map(message => (
                <Message key={message.text} {...message} />
              ))}
            </div>
          ) : null}
        </ContentContainer>
        {servicesAndFacilities ? (
          <div className="club-page__services-and-facilities">
            <ContentContainer theme={ContentContainer.themes.wide}>
              <Section
                eyebrow={servicesAndFacilitiesEyebrow}
                title={servicesAndFacilitiesTitle}
                titleLarge
                horizontal
              >
                <div className="club-page__services-and-facilities-children">
                  {servicesAndFacilities.chips.length > 1 ? (
                    <ul className="club-page__services-and-facilities-chips">
                      {servicesAndFacilities.chips.map(({ id, ...chip }) => (
                        <li key={id}>
                          <Chip
                            {...chip}
                            isSelected={selectedServicesAndFacilities === id}
                            onClick={() => setSelectedServicesAndFacilities(id)}
                            customIcon={<SvgDot />}
                          />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <div
                    className="club-page__services-and-facilities-lists"
                    key={selectedServicesAndFacilities}
                  >
                    {servicesAndFacilities.featuredLeft.length ? (
                      <div className="club-page__services-and-facilities-left-list">
                        {servicesAndFacilities.featuredLeft.map(item => (
                          <div key={item.name}>
                            <div className="club-page__icon-container">
                              <img src={item.iconHref} alt={item.iconAlt} />
                            </div>
                            <div className="club-page__services-and-facilities-text-container">
                              <Text theme="emphasis">{item.name}</Text>
                              {item.disclaimer ? (
                                <Text size="interface">{item.disclaimer}</Text>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {servicesAndFacilities.featuredRight.length ? (
                      <div className="club-page__services-and-facilities-right-list">
                        {servicesAndFacilities.featuredRight.map(item => (
                          <div key={item.name}>
                            <div className="club-page__icon-container">
                              <img src={item.iconHref} alt={item.iconAlt} />
                            </div>
                            <div className="club-page__services-and-facilities-text-container">
                              <Text theme="emphasis">{item.name}</Text>
                              {item.disclaimer ? (
                                <Text size="interface">{item.disclaimer}</Text>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  {servicesAndFacilities.fullList.length ? (
                    <>
                      <Modal {...servicesAndFacilities.modal}>
                        {
                          <div className="club-page__services-and-facilities-full-list">
                            {fullListColumns.map(column => (
                              <div
                                key={column.id}
                                className="club-page__services-and-facilities-full-list-column"
                              >
                                {column.items.map(item => (
                                  <div key={item.name}>
                                    <div className="club-page__icon-container">
                                      <img
                                        src={item.iconHref}
                                        alt={item.iconAlt}
                                      />
                                    </div>
                                    <div className="club-page__services-and-facilities-text-container">
                                      <Text theme="emphasis">{item.name}</Text>
                                      {item.disclaimer ? (
                                        <Text size="interface">
                                          {item.disclaimer}
                                        </Text>
                                      ) : null}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        }
                      </Modal>
                      <Button
                        className={cn(
                          'club-page__services-and-facilities-modal-trigger',
                          {
                            'club-page__services-and-facilities-modal-trigger--desktop':
                              servicesAndFacilities.fullList.length > 10,
                          }
                        )}
                        {...servicesAndFacilities.modalTrigger}
                        variant={Button.variants.secondary}
                        popoverTarget={servicesAndFacilities.modal.id}
                      />
                    </>
                  ) : null}
                </div>
              </Section>
            </ContentContainer>
          </div>
        ) : null}
        {concepts ? (
          <div className="club-page__concepts-section-wrapper">
            <SegmentedSection
              eyebrow={concepts.eyebrow}
              title={concepts.title}
              views={[toConceptsSegmentedSectionView(concepts.views[0])]}
            />
          </div>
        ) : null}

        {schedule?.hasResults ? (
          <ContentContainer theme="wide" className="club-page__block">
            <Section title={scheduleTitle} titleLarge>
              <GroupExerciseSchedule {...schedule} />
            </Section>
          </ContentContainer>
        ) : null}

        {teamSection ? (
          <SegmentedSection
            eyebrow={teamSection.eyebrow}
            title={teamSection.title}
            views={[toTeamSegmentedSectionView(teamSection.views[0])]}
          />
        ) : null}

        {becomeMemberSection ? (
          <div id={becomeMemberSectionId}>
            <ClubMembershipSection
              brand={brand}
              section={becomeMemberSection}
            />
          </div>
        ) : null}

        <ContentContainer theme="wide">
          <div className="club-page__plan-your-next-visit">
            <Section
              className={cn('club-page__plan-your-next-visit-section', {
                'club-page__plan-your-next-visit-section--with-map':
                  planYourNextVisit.map,
              })}
              eyebrow={planYourNextVisit.eyebrow}
              title={planYourNextVisit.title}
              titleLarge
            >
              {planYourNextVisit.map ? (
                <div className="club-page__plan-your-next-visit-map" id={mapId}>
                  <Map {...planYourNextVisit.map} />
                </div>
              ) : null}
              <div className="club-page__plan-your-next-visit-information">
                {planYourNextVisit.warningMessages
                  ? planYourNextVisit.warningMessages.map(
                      (warningMessage, index) => (
                        <div key={`${warningMessage}-${index}`}>
                          <Message
                            inline
                            theme={Message.themes.warning}
                            {...warningMessage}
                          />
                        </div>
                      )
                    )
                  : null}
                {planYourNextVisit.address ? (
                  <div className="club-page__plan-your-next-visit-sub-section-title">
                    <LocationIcon18 />
                    <Text>{planYourNextVisit.address}</Text>
                  </div>
                ) : null}
                {planYourNextVisit.parking ? (
                  <div>
                    <div className="club-page__plan-your-next-visit-sub-section-title">
                      <Parking />
                      <Text theme={Text.themes.emphasis}>
                        {planYourNextVisit.parking.title}
                      </Text>
                    </div>
                    <RichText {...planYourNextVisit.parking.body} />
                  </div>
                ) : null}
                {planYourNextVisit.links ? (
                  <div>
                    <div className="club-page__plan-your-next-visit-sub-section-title">
                      <SvgLink />
                      <Text theme={Text.themes.emphasis}>
                        {planYourNextVisit.links.title}
                      </Text>
                    </div>
                    <ul>
                      {planYourNextVisit.links.items.map(link => (
                        <li key={link.href}>
                          <Link {...link} />
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {planYourNextVisit.capacity ? (
                  <div>
                    <div className="club-page__plan-your-next-visit-sub-section-title">
                      <Activity />
                      <Text theme={Text.themes.emphasis}>
                        {planYourNextVisit.capacity.title}
                      </Text>
                    </div>
                    <div className="club-page__plan-your-next-visit-club-load-indicator">
                      <ClubLoadIndicator
                        loadFactor={loadFactor}
                        {...planYourNextVisit.capacity.indicator}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </Section>
            {planYourNextVisit.openingHours ? (
              <div className="club-page__plan-your-next-visit-opening-hours">
                <div className="club-page__plan-your-next-visit-sub-section-title">
                  <Time />
                  <Text theme={Text.themes.emphasis}>
                    {planYourNextVisit.openingHours.title}
                  </Text>
                </div>
                <OpeningHours {...planYourNextVisit.openingHours.data} />
              </div>
            ) : null}
          </div>
        </ContentContainer>

        {mediaGallery.media && mediaGallery.media.length > 1 ? (
          <ContentContainer
            theme="wide"
            className="club-page__slideshow"
            id={slideshowId}
          >
            <Section title={mediaGallery.title}>
              <Slideshow {...mediaGallery.slideshow}>
                {mediaGallery.media.map(({ image, key, video }) =>
                  video ? (
                    <EmbedYoutube key={key} {...video} />
                  ) : (
                    <DynamicImage key={key} {...image} />
                  )
                )}
              </Slideshow>
            </Section>
          </ContentContainer>
        ) : null}

        {faqs.length ? (
          <div className="club-page__faqs">
            <ContentContainer theme="wide">
              {faqs.map((faq, index) => (
                <Expander key={index} {...faq} itemRenderer={RichText} />
              ))}
            </ContentContainer>
          </div>
        ) : null}

        <ContentContainer theme="wide" className="club-page__block">
          {nearby.length ? (
            <Section eyebrow={nearbyEyebrow} title={nearbyTitle} titleLarge>
              <div
                className={cn('club-page__nearby-clubs', {
                  'club-page__nearby-clubs--multiple': nearby.length > 1,
                })}
              >
                {nearby.map(item => (
                  <LocationCard
                    key={item.title}
                    openStatus={openStatuses[item.id]}
                    {...item}
                  />
                ))}
              </div>
            </Section>
          ) : null}
        </ContentContainer>
      </div>

      {ctaBanner ? (
        <div
          className={cn(`club-page__cta-banner`, {
            'club-page__cta-banner--visible': isActionBarVisible,
          })}
        >
          <ContentContainer theme="wide">
            <div
              className={`club-page__cta-banner-content club-page__cta-banner-content--${brand}`}
            >
              <div>
                <div className="club-page__cta-banner-subtitle">
                  <Text elementName="span" theme="headline" italic>
                    {ctaBanner.subtitleFirst}
                  </Text>
                  <Text
                    elementName="span"
                    theme="headline"
                    className="club-page__cta-banner-subtitle--cta"
                    italic
                  >
                    {ctaBanner.subtitleSecond}
                  </Text>
                </div>
                <Text
                  theme="headline"
                  size="basic"
                  className="club-page__cta-banner-headline-mobile"
                  italic
                >
                  {ctaBanner.title}
                </Text>
                <Text
                  theme="headline"
                  size="headline3"
                  className="club-page__cta-banner-headline-desktop"
                  italic
                >
                  {ctaBanner.title}
                </Text>
              </div>
              <div className="club-page__cta-banner-button">
                <LinkButton variant="cta" {...ctaBanner.becomeMember} />
              </div>
            </div>
          </ContentContainer>
        </div>
      ) : null}
    </Layout>
  );
};

export default ClubPage;
